import React from "react";
//Limitless imports
import { useExpertProfileContext } from "../..";
import { useGraphAPIHook, INITIAL_MESSAGE_RESOURCES, validateMessageId } from "../../../portal";
import { calculateMessageState } from "@limitless/portal/utils/message-state-engine/MessageStateEngine";
import { GET_ONBOARDING_SUMMARY_FOR_GROUP, GET_ONBOARDING_SUMMARY, CHANGE_ONBOARDING_INVITATION, UPDATE_QUIZ_STATUS, INITIATE_BRAND_LEARNING } from "../../apis/onboarding-queries/UserOnboardingQueries";
import { INVITED } from "../../../portal/utils/Constants";
const INITIAL_MESSAGE = {
  message: {
    messageId: null
  },
  loading: false
};
const INITIAL_PRACTICE_GROUP = {
  mcqPageEnabled: false,
  brandLearningPageEnabled: false
};
const INITIAL_ONBOARDING_DETAIL = {
  loading: true,
  onboarding: {
    ...INITIAL_PRACTICE_GROUP
  },
  hasError: false
};
const INITIAL_ONBOARDING_SUMMARY = {
  loading: false,
  practiceGroups: [],
  newInvitations: [],
  completed: [],
  inProgress: [],
  waitingList: [],
  hasNoInvitations: true
};
export const useExpertOnboarding = () => {
  var _onboardingDetail$onb4, _onboardingDetail$onb5;
  const [message, setMessage] = React.useState(INITIAL_MESSAGE);
  const [optInGroup, setOptInGroup] = React.useState();
  const [onboardingDetail, setOnboardingDetail] = React.useState(INITIAL_ONBOARDING_DETAIL);
  const [onboardingSummary, setOnboardingSummary] = React.useState(INITIAL_ONBOARDING_SUMMARY);
  const {
    fetchData,
    mutateData
  } = useGraphAPIHook();
  const {
    profile
  } = useExpertProfileContext();
  const isActiveQuestion = (practiceQuestions = [], isActiveQuestions) => {
    let totalQuestions = 0;
    if (!isActiveQuestions) {
      practiceQuestions === null || practiceQuestions === void 0 ? void 0 : practiceQuestions.forEach(question => {
        if (question.isActive) {
          totalQuestions += 1;
        }
      });
      if (totalQuestions === 0) {
        return false;
      }
    }
    return true;
  };
  const hasActiveQuestions = React.useMemo(() => {
    var _onboardingDetail$onb, _onboardingDetail$onb2, _onboardingDetail$onb3;
    const isActiveQuestions = ((_onboardingDetail$onb = onboardingDetail.onboarding) === null || _onboardingDetail$onb === void 0 ? void 0 : (_onboardingDetail$onb2 = _onboardingDetail$onb.activePracticeQuestions) === null || _onboardingDetail$onb2 === void 0 ? void 0 : _onboardingDetail$onb2.length) > 0;
    return isActiveQuestion((_onboardingDetail$onb3 = onboardingDetail.onboarding) === null || _onboardingDetail$onb3 === void 0 ? void 0 : _onboardingDetail$onb3.totalAssignedPracticeQuestion, isActiveQuestions);
  }, [(_onboardingDetail$onb4 = onboardingDetail.onboarding) === null || _onboardingDetail$onb4 === void 0 ? void 0 : _onboardingDetail$onb4.totalAssignedPracticeQuestion, (_onboardingDetail$onb5 = onboardingDetail.onboarding) === null || _onboardingDetail$onb5 === void 0 ? void 0 : _onboardingDetail$onb5.activePracticeQuestions]);
  const getOnboardingSummary = () => {
    setOnboardingSummary({
      ...INITIAL_ONBOARDING_SUMMARY,
      loading: true
    });
    const options = {
      dataKey: "getOnboardingSummary",
      query: GET_ONBOARDING_SUMMARY,
      variables: {},
      functionName: "ExpertOnboardingHook getOnboardingSummary"
    };
    fetchData(options).then(result => {
      const newInvitations = result.filter(r => r.membershipState === INVITED && r.endDate === null && r.startDate);
      const waitingList = result.filter(r => r.membershipState === INVITED && r.endDate !== null && r.endDate <= new Date().getTime());
      const completed = result.filter(r => r.membershipState === "non-active" && (r.onboardingOutcomeState === "rejected" || r.onboardingOutcomeState === "promoted"));
      const inProgress = result.filter(r => r.membershipState === "active");
      const hasNoInvitations = newInvitations.length === 0 && completed.length === 0 && inProgress.length === 0 && waitingList.length === 0;
      setOnboardingSummary({
        loading: false,
        newInvitations,
        waitingList,
        completed,
        inProgress,
        hasNoInvitations,
        practiceGroups: result
      });
    }).catch(() => setOnboardingSummary(INITIAL_ONBOARDING_SUMMARY));
  };
  const getOnboardingDetail = selectedGroup => {
    //selectedGroup will be used as variables once this API is changed
    setOnboardingDetail({
      ...INITIAL_ONBOARDING_DETAIL,
      onboarding: selectedGroup,
      loading: true
    });
    const options = {
      dataKey: "getOnboardingSummaryForGroup",
      query: GET_ONBOARDING_SUMMARY_FOR_GROUP,
      variables: {
        tenantName: selectedGroup.tenantName,
        groupName: selectedGroup.groupName
      },
      functionName: "ExpertOnboardingHook getOnboardingDetail"
    };
    fetchData(options).then(result => {
      setOnboardingDetail({
        loading: false,
        onboarding: {
          ...selectedGroup,
          ...result
        },
        hasError: false
      });
    }).catch(() => setOnboardingDetail({
      ...INITIAL_ONBOARDING_DETAIL,
      loading: false,
      hasError: true
    }));
  };
  const selectOptInGroup = optInGroup => {
    setOptInGroup(optInGroup);
  };
  const selectMessage = (task, callback) => {
    setMessage({
      ...INITIAL_MESSAGE,
      loading: true,
      message: {
        messageId: task.messageId
      }
    });
    validateMessageId(task.messageId, message => {
      const messageWithState = calculateMessageState(profile, message, INITIAL_MESSAGE_RESOURCES, null);
      if (messageWithState && !messageWithState.hasError) {
        messageWithState.reviewNotes = messageWithState.reviewNotes ? messageWithState.reviewNotes : [];
        setMessage({
          ...INITIAL_MESSAGE,
          message: {
            ...INITIAL_MESSAGE.message,
            ...messageWithState
          }
        });
        if (typeof callback === "function") {
          callback();
        }
      } else {
        setMessage({
          ...INITIAL_MESSAGE
        });
        if (typeof callback === "function") {
          callback(true);
        }
      }
    });
  };
  const clearSelectedMessage = () => setMessage(INITIAL_MESSAGE);
  const clearOnboarding = () => setOnboardingDetail(INITIAL_ONBOARDING_DETAIL);
  const changeOnboardingInvitationStatus = (status, callback) => {
    const options = {
      dataKey: "changeOnboardingInvitationStatus",
      mutation: CHANGE_ONBOARDING_INVITATION,
      variables: {
        tenantName: optInGroup.tenantName,
        groupName: optInGroup.groupName,
        accepted: status
      },
      functionName: "ExpertOnboardingHook changeOnboardingInvitationStatus",
      finallyFunction: callback,
      notifyDetails: {
        errorNotification: "Failed to onboard, please contact support"
      }
    };
    mutateData(options);
  };
  const updateQuizStatus = (score, callback) => {
    const options = {
      dataKey: "updateQuizStatus",
      mutation: UPDATE_QUIZ_STATUS,
      variables: {
        tenantName: optInGroup.tenantName,
        groupName: optInGroup.groupName,
        score
      },
      functionName: "ExpertOnboardingHook updateQuizStatus",
      finallyFunction: callback,
      notifyDetails: {
        errorNotification: "Failed to save quiz, please contact support"
      }
    };
    mutateData(options).then(callback).catch(e => {
      var _e$message;
      if ((_e$message = e.message) !== null && _e$message !== void 0 && _e$message.includes("max attempts reached")) {
        callback({
          quizAttemptsLeft: 0,
          latestQuizScore: 0
        });
      } else {
        callback();
      }
    });
  };
  const initiateBrandLearning = callback => {
    const options = {
      dataKey: "initiateBrandLearning",
      mutation: INITIATE_BRAND_LEARNING,
      variables: {
        tenantName: optInGroup.tenantName,
        groupName: optInGroup.groupName
      },
      functionName: "ExpertOnboardingHook initiateBrandLearning",
      finallyFunction: callback,
      notifyDetails: {
        errorNotification: "Failed to start brand learning, please contact support"
      }
    };
    mutateData(options).then(() => {
      if (typeof callback === "function") {
        callback();
      }
    }).catch(() => {
      if (typeof callback === "function") {
        callback(true);
      }
    });
  };
  return {
    onboardingDetail,
    clearOnboarding,
    getOnboardingDetail,
    clearSelectedMessage,
    selectMessage,
    getOnboardingSummary,
    onboardingSummary,
    selectOptInGroup,
    optInGroup,
    changeOnboardingInvitationStatus,
    setOnboardingDetail,
    updateQuizStatus,
    initiateBrandLearning,
    hasActiveQuestions,
    ...message
  };
};